
import { useCostsActions } from "@contexts/costs";
import { useEffect, useMemo, useState } from "react";
import Modal from "@components/Modal";
import NewIndexer from "./newIndexer";
import { BRAZILIAN_STATES } from "@utils/brazilianStates";

const stateName = (uf) => BRAZILIAN_STATES.find(s => s.uf === uf)?.name || uf;

// "2026-04" -> "2026/04" (padrão pedido pra coluna de mês/referência).
const formatMonth = (date) => (date || '').replace('-', '/');

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatValue = (value) => currencyFormatter.format(Number(value) || 0);

const Indexers = () => {
  const [indexers, setIndexers] = useState([])
  const [loading, setLoading] = useState(false)
  const [monthSearch, setMonthSearch] = useState('')
  const [expandedGroups, setExpandedGroups] = useState([])
  const [newIndexerVisible, setNewIndexerVisible] = useState(false)
  const [editingIndexer, setEditingIndexer] = useState(null)
  const [feedback, setFeedback] = useState(null)
  const { getIndexers } = useCostsActions();

  const load = async () => {
    setLoading(true)
    const res = await getIndexers(monthSearch ? { month: monthSearch } : {});
    if (!res || res.message) {
      setFeedback({ ok: false, message: res?.message || 'Não foi possível carregar os índices.' })
    }
    setIndexers(res?.res || [])
    setLoading(false)
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthSearch])

  // Mesma apresentação de "Construtoras e Obras": Estado+Tipo é a "construtora" (grupo),
  // cada mês cadastrado é uma "obra" dentro do grupo — do mais recente pro mais antigo, ao
  // contrário da listagem de obras (que é alfabética). Documentos antigos não têm `type` —
  // mesma regra de cubSelector.jsx: ausência conta como CUB.
  const groups = useMemo(() => {
    const byGroup = {}
    for (const item of indexers) {
      const type = item.type || 'CUB'
      const key = `${type}|${item.state}`
      byGroup[key] = byGroup[key] || { key, type, state: item.state, items: [] }
      byGroup[key].items.push(item)
    }
    return Object.values(byGroup)
      .map(group => ({
        ...group,
        items: [...group.items].sort((a, b) => (b.date || '').localeCompare(a.date || '')),
      }))
      .sort((a, b) => (
        stateName(a.state).localeCompare(stateName(b.state), 'pt-BR') || a.type.localeCompare(b.type, 'pt-BR')
      ))
  }, [indexers])

  const toggleGroup = (key) => {
    setExpandedGroups(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
  }

  const handleCreated = async (result) => {
    if (result?.ok) {
      setFeedback({ ok: true, message: result.message })
      setNewIndexerVisible(false)
      setEditingIndexer(null)
      await load()
    } else {
      setFeedback({ ok: false, message: result?.message || 'Não foi possível salvar o índice.' })
    }
  }

  return (
    <div className="directory-page">
      {newIndexerVisible && (
        <Modal close={() => setNewIndexerVisible(false)}>
          <NewIndexer onSuccess={handleCreated} />
        </Modal>
      )}
      {editingIndexer && (
        <Modal close={() => setEditingIndexer(null)}>
          <NewIndexer indexer={editingIndexer} onSuccess={handleCreated} />
        </Modal>
      )}

      <div className="directory-header">
        <h2>Indexadores</h2>
        <div className="directory-actions">
          <label className="month-search">
            Buscar por mês
            <input type="month" value={monthSearch} onChange={(e) => setMonthSearch(e.target.value)} />
          </label>
          {monthSearch && (
            <button type="button" className="button" onClick={() => setMonthSearch('')}>Limpar</button>
          )}
          <button type="button" className="button button-accent" onClick={() => setNewIndexerVisible(true)}>
            Novo Índice
          </button>
        </div>
      </div>

      {feedback && (
        <p className={feedback.ok ? "directory-feedback directory-feedback--ok" : "directory-feedback directory-feedback--error"}>
          {feedback.message}
        </p>
      )}

      {loading && <p>Carregando...</p>}

      {!loading && (
        <div className="directory-list">
          {groups.map(group => {
            const expanded = expandedGroups.includes(group.key)
            return (
              <div className="directory-company" key={group.key}>
                <div
                  role="button"
                  tabIndex={0}
                  className={`directory-company-row ${expanded ? "expanded" : ""}`}
                  onClick={() => toggleGroup(group.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleGroup(group.key) }}
                >
                  <span className="directory-company-name">{stateName(group.state)}</span>
                  <span className="directory-company-erp">{group.type}</span>
                  <span className="directory-company-count">{group.items.length} mês(es)</span>
                </div>

                {expanded && (
                  group.items.length === 0 ? (
                    <p className="directory-empty">Nenhum mês cadastrado.</p>
                  ) : (
                    <table className="directory-buildings">
                      <thead>
                        <tr>
                          <th>Ref (AAAA/MM)</th>
                          <th>Valor</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map(item => (
                          <tr key={`${item.state}-${item.date}`}>
                            <td>{formatMonth(item.date)}</td>
                            <td>{formatValue(item.value)}</td>
                            <td>
                              <button
                                type="button"
                                className="button button-accent"
                                onClick={() => setEditingIndexer({ ...item, type: group.type })}
                              >
                                Editar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
};

export default Indexers;
